<template>
    <DForm @submit.prevent="submitForm">
        <DFormRow
            :label="__('settings.net_visor_settings.form.url')"
            :is-required="true"
            :error="errorsData.url"
            :hint="__('settings.net_visor_settings.form.url_hint')"
        >
            <DInput
                id="url"
                v-model="form.url"
                :placeholder="__('settings.net_visor_settings.form.url')"
                :is-valid="isFormFieldValid('url')"
                maxlength="128"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.net_visor_settings.form.sender')"
            :is-required="true"
            :error="errorsData.sender"
            :hint="__('settings.net_visor_settings.form.sender_hint')"
        >
            <DInput
                id="sender"
                v-model="form.sender"
                :placeholder="__('settings.net_visor_settings.form.sender')"
                :is-valid="isFormFieldValid('sender')"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.net_visor_settings.form.user')"
            :is-required="true"
            :error="errorsData.user"
            :hint="__('settings.net_visor_settings.form.user_hint')"
        >
            <DInput
                id="user"
                v-model="form.user"
                :placeholder="__('settings.net_visor_settings.form.user')"
                :is-valid="isFormFieldValid('user')"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.net_visor_settings.form.user_key')"
            :is-required="netVisorSettings === null"
            :error="errorsData.user_key"
            :hint="__('settings.net_visor_settings.form.user_key_hint')"
        >
            <DInput
                id="user_key"
                v-model="form.user_key"
                :placeholder="__('settings.net_visor_settings.form.user_key')"
                type="password"
                :is-valid="isFormFieldValid('user_key')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.net_visor_settings.form.partner')"
            :is-required="true"
            :error="errorsData.partner"
            :hint="__('settings.net_visor_settings.form.partner_hint')"
        >
            <DInput
                id="partner"
                v-model="form.partner"
                :placeholder="__('settings.net_visor_settings.form.partner')"
                :is-valid="isFormFieldValid('partner')"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.net_visor_settings.form.partner_key')"
            :is-required="netVisorSettings === null"
            :error="errorsData.partner_key"
            :hint="__('settings.net_visor_settings.form.partner_key_hint')"
        >
            <DInput
                id="partner_key"
                v-model="form.partner_key"
                :placeholder="__('settings.net_visor_settings.form.partner_key')"
                type="password"
                :is-valid="isFormFieldValid('partner_key')"
                maxlength="255"
            />
        </DFormRow>
        <DFormRow
            :label="__('settings.net_visor_settings.form.organisation_id')"
            :is-required="true"
            :error="errorsData.organisation_id"
            :hint="__('settings.net_visor_settings.form.organisation_id_hint')"
        >
            <DInput
                id="organisation_id"
                v-model="form.organisation_id"
                :placeholder="__('settings.net_visor_settings.form.organisation_id')"
                :is-valid="isFormFieldValid('organisation_id')"
            />
        </DFormRow>
        <DRow>
            <DCol sm="4" />
            <DCol sm="4">
                <div
                    v-if="connectionStatus === 'successful'"
                    class="connection-successful"
                >
                    {{ __('settings.net_visor_settings.connection_successful') }}
                </div>
                <div
                    v-if="connectionStatus === 'unsuccessful'"
                    class="connection-failed"
                >
                    {{ __('settings.net_visor_settings.connection_unsuccessful') }}
                </div>
            </DCol>
            <DCol
                sm="4"
                class="check-database-connection-btn"
            >
                <DButton
                    icon="lan-connect"
                    :label="__('settings.net_visor_settings.check_connection')"
                    :disabled="isInLoadingState"
                    @click="checkConnection"
                />
            </DCol>
        </DRow>
        <DFormControls
            :dirty="form.isDirty"
            @submit="submitForm"
        />
        <DConnectionErrorModal
            :show-modal="connectionException !== null"
            :exception="connectionException"
            @close-modal="closeErrorCheckModal"
        />
    </DForm>
</template>

<script setup>
import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'
import { useForm } from '@inertiajs/vue3'

import { route } from '~@/Composables/route'
import { ref, watch } from 'vue'

const props = defineProps({
    netVisorSettings: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})

const emit = defineEmits(['form-submitted'])

const form = useForm({
    url: props.netVisorSettings?.url ?? null,
    sender: props.netVisorSettings?.sender ?? null,
    user: props.netVisorSettings?.user ?? null,
    user_key: props.netVisorSettings?.user_key ?? null,
    partner: props.netVisorSettings?.partner ?? null,
    partner_key: props.netVisorSettings?.partner_key ?? null,
    organisation_id: props.netVisorSettings?.organisation_id ?? null,
    finance_system: 'net_visor'
})

const connectionStatus = ref('')
const connectionException = ref(null)
const credentialsAreValid = ref(false)
const errorsData = ref(props.errors)

watch(() => props.errors, (newErrors, oldErrors) => {
    errorsData.value = newErrors
})

function checkConnection () {
    errorsData.value = {}
    connectionException.value = null
    window.axios.post(route('settings.finance-system-settings.check-netvisor', form))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }

            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

function closeErrorCheckModal () {
    connectionException.value = null
}

function submitForm () {
    emit('form-submitted', form)
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 25px;
}
.connection-successful {
    color: green;
}
.connection-failed {
    color: red;
}
</style>
